import React from 'react';
import Modal from 'shared/components/modal';
import Address from 'shared/account-settings/components/address-verification-modals/address';
import {AddressForm} from 'src/types/onboarding';
import {useFormikContext} from 'formik';
import RadioButton from 'src/components/radio-button';
import {diffChars} from 'diff';
import {useBoolean} from 'src/hooks/useBoolean';
import getTextDifferences from 'src/utils/getTextDifferences';

interface Props {
  onUpdate?: () => void;
  confirmationText?: string;
}

export default function SuggestAddressModal({
  confirmationText,
  onUpdate,
}: Props): React.ReactElement {
  const form = useFormikContext<AddressForm>();
  const useSuggestion = useBoolean(false);

  function forceClose(): void {
    form.setFieldValue('_addressSuggestion', undefined);
  }

  function onSubmit(): void {
    if (useSuggestion.value) {
      form.setValues({
        ...form.values,
        ...form.values._addressSuggestion,
        addressVerified: true,
        confirmedUnverifiedAddress: false,
        _addressSuggestion: undefined,
      });
    } else {
      form.setValues({
        ...form.values,
        addressVerified: false,
        confirmedUnverifiedAddress: true,
        _addressSuggestion: undefined,
      });
    }

    onUpdate?.();
  }

  const suggestedAddress = {...form.values._addressSuggestion} as Record<string, unknown>;
  const keysToCheck: ('addressLine1' | 'addressLine2' | 'city' | 'subdivision' | 'postalCode')[] = [
    'addressLine1',
    'addressLine2',
    'city',
    'subdivision',
    'postalCode',
  ];
  keysToCheck.forEach((key) => {
    const differences = diffChars(
      (form.values[key] as string) || '',
      form.values._addressSuggestion ? (form.values._addressSuggestion[key] as string) || '' : ''
    );
    suggestedAddress[key] = getTextDifferences(differences);
  });

  return (
    <div>
      <Modal show onClose={forceClose} className="address-verification-modal">
        <Modal.TextHeader onClose={forceClose}>
          Please pick a valid matching address
        </Modal.TextHeader>
        <Modal.Body>
          <p className="address-verification-modal-text">
            There&apos;s a problem with the address provided - we&apos;ve marked our suggestions in
            green below.
          </p>
          <fieldset role="radiogroup" aria-label="Please select an address">
            <RadioButton
              label={[<Address type="Original" key="original-address" address={form.values} />]}
              name="address"
              onChange={useSuggestion.setFalse}
              checked={!useSuggestion.value}
              cy="originalAddress"
            />
            <hr aria-hidden="true" />
            <RadioButton
              label={[
                <Address type="Suggested" key="suggested-address" address={suggestedAddress} />,
              ]}
              name="address"
              onChange={useSuggestion.setTrue}
              checked={useSuggestion.value}
              cy="suggestedAddress"
            />
          </fieldset>

          <hr aria-hidden="true" />
        </Modal.Body>
        <Modal.Footer
          primaryButton={{
            text: confirmationText || 'Use Selected Address',
            onClick: onSubmit,
            cy: 'useSelectedAddress',
          }}
        />
      </Modal>
    </div>
  );
}
